import axios, {
  AxiosRequestConfig,
  Method as HTTPMethod,
  AxiosError,
  AxiosResponse,
} from 'axios';
import { history } from '../history';

axios.interceptors.response.use(undefined, handleNetworkError);
// remove default Axios content-type of 'application/x-www-form-urlencoded'
// this allows us to leave the content-type undefined when submitting files and
// a content type of 'multipart/form-data; boundary=... is automatically generated by the browser
// mock axios doesn't have default headers
if (axios.defaults.headers != null) {
  if (axios.defaults.headers.put != null) {
    delete axios.defaults.headers.put['Content-Type'];
  }
  if (axios.defaults.headers.post != null) {
    delete axios.defaults.headers.post['Content-Type'];
  }
}

export function getHeaders(jwtToken: string) {
  return {
    Authorization: `Bearer ${jwtToken}`,
    'Content-Type': 'application/json',
    Accept: 'application/json',
  };
}

/**
 * Convert an object to a URL query string.
 * Object properties get `[]` appending to each duplicate key name.
 */
export function stringify(args: object): string {
  const params: string[] = [];

  Object.keys(args).forEach(key => {
    const value = args[key];

    if (typeof value === 'undefined' || value === null) return;

    if (value && typeof value === 'object') {
      Object.keys(value).forEach(subkey => {
        params.push(`${key}[]=${encodeURIComponent(args[key][subkey])}`);
      });
    } else {
      params.push(`${key}=${encodeURIComponent(args[key])}`);
    }
  });

  return params.join('&');
}

/**
 * A copy of the Node.js 'querystring' utility via https://github.com/Gozala/querystring
 * Convert an object a to a URL query string.
 * Object properties do NOT get anything appending to each duplicate key name,
 * unlike 'stringify()'
 */
export function stringifyNode(
  obj: any,
  sep?: string,
  eq?: string,
  name?: string,
): string {
  sep = sep || '&';
  eq = eq || '=';
  if (obj === null) {
    obj = undefined;
  }

  if (typeof obj === 'object') {
    return Object.keys(obj)
      .map(function (k) {
        const ks = encodeURIComponent(k) + eq;
        if (Array.isArray(obj[k])) {
          return obj[k]
            .map(function (v) {
              return ks + encodeURIComponent(stringifyPrimitive(v));
            })
            .join(sep);
        } else {
          return ks + encodeURIComponent(stringifyPrimitive(obj[k]));
        }
      })
      .filter(Boolean)
      .join(sep);
  }

  if (!name) return '';
  return (
    encodeURIComponent(String(name)) +
    eq +
    encodeURIComponent(stringifyPrimitive(obj))
  );
}

function stringifyPrimitive(v) {
  switch (typeof v) {
    case 'string':
      return v;
    case 'boolean':
      return v ? 'true' : 'false';
    case 'number':
      return isFinite(v) ? v : '';
    default:
      return '';
  }
}

export function parse(urlOrSearch: string): { [key: string]: any } {
  const search = urlOrSearch.split('?')[1];
  const result = {};

  if (!search || typeof search !== 'string') {
    return result;
  }

  search.split('&').forEach(s => {
    let [key, value] = s.split('=');
    if (key) key = decodeURIComponent(key);

    if (value) value = value.replace(/\+/g, '%20');
    if (value) value = decodeURIComponent(value);

    if (result[key]) {
      if (!Array.isArray(result[key])) {
        result[key] = [result[key]];
      }

      result[key].push(value);
    } else {
      result[key] = value;
    }
  });

  return result;
}

export function originUrl(append = ''): string {
  let origin =
    process.env.NODE_ENV === 'test'
      ? process.env.E2E_BASE_URL
      : window.location.origin;

  // fix for IE
  if (!origin && process.env.NODE_ENV !== 'test') {
    const portOrEmpty = window.location.port ? `:${window.location.port}` : '';
    origin = `${window.location.protocol}//${window.location.hostname}${portOrEmpty}`;
  }

  return `${origin}/#/${append}`;
}

function handleNetworkError(error: AxiosError) {
  const status = error && error.response && error.response.status;
  switch (status) {
    case 401:
      history.push('/logout');
      break;
    default:
      break;
  }

  return Promise.reject(error);
}

export function buildRequest(baseUrl: string, method: HTTPMethod) {
  return function <T = any>(
    token: string,
    endpoint: string,
    bodyOrQuery?: object,
    transformResponse?: (res: AxiosResponse<any>) => T,
    transformError?: (res: AxiosError<any>) => T,
  ): Promise<T> {
    return new Promise((resolve, reject) => {
      let url = baseUrl;
      url += endpoint.slice(0, 1) === '/' ? '' : '/';
      url += endpoint;

      const config: AxiosRequestConfig &
        Required<Pick<AxiosRequestConfig, 'url' | 'method' | 'headers'>> = {
        url: url,
        method: method,
        headers: getHeaders(token),
      };

      if (bodyOrQuery && method !== 'get') {
        if (bodyOrQuery instanceof File) {
          const file = bodyOrQuery as File;
          const formData = new FormData();
          formData.append('name', file.name);
          formData.append('file', file);
          config.data = formData;
          delete config.headers['Content-Type'];
        } else if (bodyOrQuery['file'] instanceof File) {
          const { file, ...body } = bodyOrQuery as {
            file: File;
            [key: string]: any;
          };
          const formData = new FormData();
          formData.append('name', file.name);
          formData.append('file', file);
          Object.keys(body).forEach(key => {
            formData.append(key, body[key]);
          });
          config.data = formData;
          delete config.headers['Content-Type'];
        } else {
          config.data = bodyOrQuery;
        }
      } else if (bodyOrQuery && Object.keys(bodyOrQuery).length) {
        if (!config.url.includes('?')) {
          config.url += '?';
        }

        config.url += stringify(bodyOrQuery);
      }

      axios(config)
        .then(res => {
          resolve(transformResponse ? transformResponse(res) : res.data);
        })
        .catch(error => {
          if (error?.response?.status !== 401) {
            reject(
              transformError
                ? transformError(error)
                : error?.response?.data ?? error,
            );
          }
          /* When the response is a 401 Unauthorized error,
           * we intentionally DO NOT reject or resolve the promise.
           * This is because the axios interceptor `handleNetworkError` is handling 401s with logout redirect,
           * and we do not want the requesting code to _also_ try to handle the 401 error.
           */
        });
    });
  };
}

export function buildApi(baseUrl: string) {
  return {
    get: buildRequest(baseUrl, 'get'),
    put: buildRequest(baseUrl, 'put'),
    post: buildRequest(baseUrl, 'post'),
    delete: buildRequest(baseUrl, 'delete'),
  };
}
